import { FetchResult } from '@apollo/client';
import { formatISO } from 'date-fns';
import { Mutation, NoteInput } from 'sr-types/lib/note/v1/graphql';
import type { Name, Note, Reference } from 'sr-types/lib/search/v1/graphql';
import { getFullName } from '../../person/person';
import { AllPermissions, UserProfile } from '../auth/api';
import { PUNCTUATION_MARKS_CONSTANTS } from '../constants/constants';
import { truncateText } from '../utils/commonUtils';
import { toISOFromJSDate } from '../utils/dateTime';
export { stripHtmlToText } from '../utils/html';

export const getEmptyNote = (entityReference: Reference, userProfile: UserProfile): Note => {
    const currentDate = new Date();
    return {
        title: '',
        content: '',
        entityReference,
        security: {
            allowedActions: [AllPermissions.Read, AllPermissions.Edit, AllPermissions.Delete, AllPermissions.Share]
        },
        createdOn: toISOFromJSDate(currentDate),
        createdByName: `${userProfile.name.firstName} ${userProfile.name.lastName}`,
        updatedByName: `${userProfile.name.firstName} ${userProfile.name.lastName}`,
        updatedOn: toISOFromJSDate(currentDate),
        headline: '',
        id: '',
        name: '',
        uri: '',
        labels: [],
        contentType: 'text/html'
    };
};

/**
 * Derives a note title from rich-text or HTML content by taking the first
 * non-empty text line.
 *
 * The function normalizes common HTML entities, converts line-break-like tags
 * (for example, <br> and closing block tags) into newline separators, strips
 * remaining HTML tags, and then returns the first trimmed non-empty line.
 *
 * @param content Raw note content, typically HTML/rich-text markup.
 * @returns The first meaningful text line, or an empty string when content has no text.
 */
export const deriveNoteTitleFromContent = (content: string): string => {
    if (!content) return '';

    const normalized = content
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/<br\s*\/?>/gi, PUNCTUATION_MARKS_CONSTANTS.newLine)
        .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, PUNCTUATION_MARKS_CONSTANTS.newLine)
        .replace(/<[^>]+>/g, '')
        .replace(/\r/g, '');

    return truncateText(
        normalized
            .split(PUNCTUATION_MARKS_CONSTANTS.newLine)
            .map((line) => line.replace(/\s+/g, ' ').trim())
            .find((line) => line.length > 0) || ''
    );
};

export const stateToNoteInput = (note: Note, entityReference: Reference): NoteInput => {
    return {
        title: note.title,
        content: note.content,
        entityReference: note?.entityReference || entityReference,
        identity: { id: note?.id || '' },
        labels: note.labels,
        contentType: note.contentType
    };
};

export const setNoteAfterSave = (
    modifiedNote: NoteInput,
    originalNote: Note,
    noteSaveResponse: FetchResult<Pick<Mutation, 'saveNote'>>
) => {
    return {
        id: noteSaveResponse.data.saveNote.id,
        reference: noteSaveResponse.data.saveNote.reference,
        content: modifiedNote.content,
        title: modifiedNote.title,
        name: modifiedNote.title,
        entityReference: modifiedNote.entityReference,
        security: originalNote.security,
        createdOn: originalNote.createdOn,
        createdByName: originalNote.createdByName,
        updatedByName: originalNote.updatedByName,
        updatedOn: formatISO(new Date()), // This could be different from service, but we don't care.
        headline: '',
        uri: '',
        labels: modifiedNote.labels
    };
};

export const makeStatusNoteIdFirst = (statusNoteId: string, notes: Note[]) => {
    const isStatusNotePresent = statusNoteId && notes.length > 1 && notes.some((n) => n.id === statusNoteId);

    if (isStatusNotePresent) {
        const statusNote = notes.find((n) => n.id === statusNoteId);
        const filtered = notes.filter((n) => n.id !== statusNoteId);

        return [statusNote, ...filtered];
    } else {
        return notes;
    }
};

export const syncStatusNoteIntoNotes = (
    notes: Note[],
    statusNote: Note | null | undefined,
    deletedStatusNoteId?: string,
    updatedByName?: Name
): Note[] => {
    let updated = notes;

    if (deletedStatusNoteId) {
        updated = updated.filter((n) => n.id !== deletedStatusNoteId);
    }

    const statusNoteId = statusNote?.id ?? statusNote?.reference?.id;
    if (!statusNote || !statusNoteId) {
        return updated;
    }

    const existing = updated.find((n) => n.id === statusNoteId);

    if (existing) {
        if (existing.content !== statusNote.content) {
            updated = updated.map((n) =>
                n.id === statusNoteId
                    ? {
                          ...n,
                          content: statusNote.content,
                          labels: statusNote.labels ?? n.labels,
                          updatedOn: statusNote.updatedOn ?? n.updatedOn,
                          updatedByName: getNoteOwner(n.updatedByName, updatedByName)
                      }
                    : n
            );
        }
    } else if (statusNote.content) {
        updated = [statusNote, ...updated];
    }

    return makeStatusNoteIdFirst(statusNoteId, updated);
};

export const getNoteOwner = (currentNoteOwner: string, userWhoEdited: Name) => {
    return userWhoEdited ? getFullName(userWhoEdited) : currentNoteOwner;
};

export const getAvailableTagsFromNotes = (notes: Note[]): string[] =>
    [...new Set(notes.flatMap((n) => n.labels || []))].sort();

export const noteMatchesFilter = (note: Note, term: string, selectedTags: string[]): boolean => {
    const matchesSearch =
        !term.length ||
        note.content.toLowerCase().includes(term.toLowerCase()) ||
        note.title.toLowerCase().includes(term.toLowerCase());
    const matchesTags = !selectedTags.length || selectedTags.some((tag) => note.labels?.includes(tag));
    return matchesSearch && matchesTags;
};
